import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { AuthState, LoginMethod, UserProfile } from '../types/auth';
import { supabase } from '../lib/supabaseClient';

type AuthContextValue = {
  state: AuthState;
  login: (method: LoginMethod) => Promise<UserProfile>;
  logout: () => void;
  updateProfile: (update: Partial<Pick<UserProfile, 'displayName' | 'photoURL'>>) => void | Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function generateId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({ user: null, loading: true });
  const loadingProfileRef = useRef<null | string>(null);

  async function loadAndSetProfile(userId: string, defaults: Partial<UserProfile>): Promise<void> {
    if (loadingProfileRef.current === userId) return;
    loadingProfileRef.current = userId;
    setState((prev) => ({ ...prev, loading: true }));
    const profile = await ensureProfile(userId, defaults);
    setState({ user: profile, loading: false });
    loadingProfileRef.current = null;
  }

  async function ensureProfile(userId: string, defaults: Partial<UserProfile> = {}): Promise<UserProfile> {
    try {
      const { data: row, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      if (error && error.code !== 'PGRST116') {
        // ignore if table missing
      }
      const now = Date.now();
      const defaultDisplayName = typeof defaults.displayName === 'string' && defaults.displayName.trim() ? defaults.displayName : 'User';
      const defaultEmail = typeof defaults.email === 'string' ? defaults.email : undefined;
      const defaultPhoto = typeof defaults.photoURL === 'string' ? defaults.photoURL : undefined;
      const defaultProvider = (defaults.provider ?? 'email') as UserProfile['provider'];

      const defaultRole: UserProfile['role'] =
        typeof defaults.role === 'string'
          ? defaults.role
          : (defaultEmail && import.meta.env.VITE_ADMIN_EMAIL && defaultEmail.toLowerCase() === (import.meta.env.VITE_ADMIN_EMAIL as string).toLowerCase()
              ? 'admin'
              : undefined);
      const defaultIsActive = typeof defaults.isActive === 'boolean' ? defaults.isActive : true;
      const defaultArtistRate = typeof defaults.artistRate === 'number' ? defaults.artistRate : 0.2;
      const defaultAnchor = typeof defaults.paymentCycleAnchor === 'string' ? defaults.paymentCycleAnchor : new Date().toISOString().slice(0, 10);

      const base: UserProfile = {
        id: userId,
        displayName: defaultDisplayName,
        email: defaultEmail,
        photoURL: defaultPhoto,
        provider: defaultProvider,
        role: defaultRole,
        isActive: defaultIsActive,
        artistRate: defaultArtistRate,
        paymentCycleAnchor: defaultAnchor,
        createdAt: now,
        lastLoginAt: now,
      };

      // Always check artist_profiles for role/rate info regardless of profiles row
      let artistRow: {
        role?: 'admin' | 'artist' | null;
        is_active?: boolean | null;
        artist_rate?: number | null;
        payment_cycle_anchor?: string | null;
        display_name?: string | null;
      } | null = null;
      try {
        const { data } = await supabase
          .from('artist_profiles')
          .select('role, is_active, artist_rate, payment_cycle_anchor, display_name')
          .eq('id', userId)
          .single();
        artistRow = (data || null) as typeof artistRow;
      } catch {
        // ignore if artist_profiles table missing
      }

      if (!row) {
        try {
          await supabase.from('profiles').upsert({
            id: userId,
            display_name: base.displayName,
            photo_url: base.photoURL || null,
          });
        } catch {}
        return {
          ...base,
          displayName: (artistRow?.display_name && typeof artistRow.display_name === 'string') ? artistRow.display_name : base.displayName,
          role: artistRow?.role ?? defaultRole,
          isActive: typeof artistRow?.is_active === 'boolean' ? artistRow.is_active : defaultIsActive,
          artistRate: typeof artistRow?.artist_rate === 'number' ? artistRow.artist_rate : defaultArtistRate,
          paymentCycleAnchor: typeof artistRow?.payment_cycle_anchor === 'string' ? artistRow.payment_cycle_anchor : defaultAnchor,
        };
      }
      type ProfileRow = { id: string; display_name?: string | null; photo_url?: string | null; };
      const typed = (row || {}) as ProfileRow;

      return {
        id: typed.id,
        displayName: (typed.display_name && typeof typed.display_name === 'string' ? typed.display_name : defaultDisplayName),
        email: defaultEmail,
        photoURL: (typed.photo_url && typeof typed.photo_url === 'string' ? typed.photo_url : defaultPhoto),
        provider: defaultProvider,
        role: artistRow?.role ?? defaultRole,
        isActive: typeof artistRow?.is_active === 'boolean' ? artistRow?.is_active : defaultIsActive,
        artistRate: typeof artistRow?.artist_rate === 'number' ? artistRow?.artist_rate : defaultArtistRate,
        paymentCycleAnchor: typeof artistRow?.payment_cycle_anchor === 'string' ? artistRow?.payment_cycle_anchor : defaultAnchor,
        createdAt: base.createdAt,
        lastLoginAt: now,
      };
    } catch {
      const now = Date.now();
      const defaultDisplayName = typeof defaults.displayName === 'string' && defaults.displayName.trim() ? defaults.displayName : 'User';
      const defaultEmail = typeof defaults.email === 'string' ? defaults.email : undefined;
      const defaultPhoto = typeof defaults.photoURL === 'string' ? defaults.photoURL : undefined;
      const defaultProvider = (defaults.provider ?? 'email') as UserProfile['provider'];
      const fallbackRole: UserProfile['role'] =
        typeof defaults.role === 'string'
          ? defaults.role
          : (defaultEmail && import.meta.env.VITE_ADMIN_EMAIL && defaultEmail.toLowerCase() === (import.meta.env.VITE_ADMIN_EMAIL as string).toLowerCase()
              ? 'admin'
              : undefined);
      const fallbackIsActive = typeof defaults.isActive === 'boolean' ? defaults.isActive : true;
      const fallbackArtistRate = typeof defaults.artistRate === 'number' ? defaults.artistRate : 0.2;
      const fallbackAnchor = typeof defaults.paymentCycleAnchor === 'string' ? defaults.paymentCycleAnchor : new Date().toISOString().slice(0, 10);
      return {
        id: userId,
        displayName: defaultDisplayName,
        email: defaultEmail,
        photoURL: defaultPhoto,
        provider: defaultProvider,
        role: fallbackRole,
        isActive: fallbackIsActive,
        artistRate: fallbackArtistRate,
        paymentCycleAnchor: fallbackAnchor,
        createdAt: now,
        lastLoginAt: now,
      };
    }
  }

  useEffect(() => {
    let mounted = true;
    const syncFromSession = async (sess: any | null) => {
      if (!mounted) return;
      if (sess?.user) {
        const u = sess.user;
        await loadAndSetProfile(u.id, {
          displayName: (u.user_metadata as any)?.full_name || u.email?.split('@')[0],
          email: u.email || undefined,
          photoURL: (u.user_metadata as any)?.avatar_url,
          provider: (u.app_metadata?.provider as any) || 'email',
        });
      } else {
        setState({ user: null, loading: false });
      }
    };

    supabase.auth.getSession().then(({ data }) => syncFromSession(data?.session ?? null));
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      syncFromSession(session);
    });
    setState({ user: null, loading: true });
    return () => { mounted = false; sub.subscription.unsubscribe(); };
  }, []);

  useEffect(() => {
    function onVisibilityChange() {
      if (document.visibilityState === 'visible') {
        supabase.auth.getUser().then(async ({ data }) => {
          const user = data?.user;
          if (user) {
            const profile = await ensureProfile(user.id, {
              displayName: (user.user_metadata as any)?.full_name || user.email?.split('@')[0],
              email: user.email || undefined,
              photoURL: (user.user_metadata as any)?.avatar_url,
              provider: (user.app_metadata?.provider as any) || 'email',
            });
            setState({ user: profile, loading: false });
          }
        });
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  const login = useCallback(async (method: LoginMethod): Promise<UserProfile> => {
    if (method.type === 'email') {
      const { email, password } = method.credentials;
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      const sUser = data.user!;
      if (state.user?.provider === 'anonymous') {
        setState({ user: null, loading: true });
      }
      const profile = await ensureProfile(sUser.id, {
        displayName: (sUser.user_metadata as any)?.full_name || email.split('@')[0],
        email,
        photoURL: (sUser.user_metadata as any)?.avatar_url,
        provider: (sUser.app_metadata?.provider as any) || 'email',
      });
      setState({ user: profile, loading: false });
      return profile;
    }
    if (method.type === 'google' || method.type === 'apple') {
      const provider = method.type;
      const { error } = await supabase.auth.signInWithOAuth({ provider });
      if (error) throw error;
      return new Promise<UserProfile>((resolve) => resolve(state.user as UserProfile));
    }
    const now = Date.now();
    const user: UserProfile = { id: generateId(), displayName: 'Guest', provider: 'anonymous', createdAt: now, lastLoginAt: now };
    setState({ user, loading: false });
    return user;
  }, [state.user]);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setState({ user: null, loading: false });
  }, []);

  const updateProfile = useCallback(async (update: Partial<Pick<UserProfile, 'displayName' | 'photoURL'>>) => {
    if (!state.user) return;
    const userId = state.user.id;
    try {
      await supabase.from('profiles').update({ display_name: update.displayName, photo_url: update.photoURL }).eq('id', userId);
    } catch {}
    setState((prev) => (prev.user ? { ...prev, user: { ...prev.user!, ...update } } : prev));
  }, [state.user]);

  const value = useMemo<AuthContextValue>(() => ({ state, login, logout, updateProfile }), [state, login, logout, updateProfile]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}


